<template>
  <div
    class="m-featured-event"
    :style="{
      backgroundImage: backgroundImage ? `url(${backgroundImage})` : 'none'
    }"
  >
    <div class="m-featured-event--header">
      <span class="m-title">
        {{ matchLabelTitle || 'Feature Matches' }}
      </span>

      <IconPlay
        v-if="event.hasLive"
        class="label-tag"
      />
      <!-- commentary -->
      <IconCommentary
        v-else-if="isLive && event.hasLiveCoverage"
        class="label-tag"
      />
      <!-- top team -->
      <IconHot
        v-if="event.isTopTeam"
        class="label-tag"
      />
    </div>

    <router-link
      :class="['m-sport-link', {
        'm-sport-live': isLive
      }]"
      :to="matchLink"
      @click.native="prefillMatchDetail"
    >
      <div class="m-featured-event--info">
        <div class="tw-w-[67.74%] tw-flex tw-justify-between">
          <div class="teams-wrap">
            <div class="m-team">
              <img
                v-if="teamLogoVisible"
                class="m-team-logo"
                :src="event.homeTeamIcon"
                loading="lazy"
                alt=""
              >
              <div class="m-team-name tw-w-[138px] tw-truncate">
                {{ event.homeTeam }}
              </div>
            </div>

            <div class="m-team">
              <img
                v-if="teamLogoVisible"
                class="m-team-logo"
                :src="event.awayTeamIcon"
                loading="lazy"
                alt=""
              >
              <div class="m-team-name tw-w-[138px] tw-truncate">
                {{ event.awayTeam }}
              </div>
            </div>
          </div>

          <div
            v-if="isLive"
            class="m-score-group"
          >
            <!-- homeTeam 比分 -->
            <div class="m-score">
              {{ scores[0] || '-' }}
            </div>
            <!-- awayTeam 比分 -->
            <div class="m-score away-score">
              {{ scores[1] || '-' }}
            </div>
          </div>
        </div>

        <div>
          <div
            class="m-time"
            :class="{
              'm-live-time': isLive
            }"
          >
            {{ matchTime }}
          </div>
          <div class="m-date">
            {{ isLive ? matchStage : showDate }}
          </div>
        </div>
      </div>
    </router-link>

    <div class="m-featured-event--outcomes">
      <template v-if="market && outcomes && outcomes.order">
        <Outcome
          v-for="(id, index) in outcomes.order"
          :key="index"
          :market="market"
          :outcome="outcomes.map[id]"
          :event="event"
          show-desc
          :description="marketTitles[index]"
          @click="handleOdds"
        />
      </template>

      <!-- 有一种情况是后台压根没有传递market，但是这个market还是要显示,所以取固定数据显示这个market -->
      <template v-else>
        <template v-if="marketTitles">
          <div
            v-for="one in marketTitles"
            :key="one"
            class="m-outcome disabled"
          >
            <img
              width="14px"
              src="~public/images/icon/locked.svg"
            >
          </div>
        </template>
      </template>
    </div>

    <div class="featured-event--footer">
      <router-link
        class="m-view-more x-link-active"
        :to="{
          name: 'matchList',
          params: {
            sportName: 'Soccer'
          },
          query: {
            t: event.tournamentId
          }
        }"
        @click.native="handleMore"
      >
        <span>More Events({{ count }}) </span>
        <svg-icon icon-class="icon-arrowright-new" />
      </router-link>
    </div>
  </div>
</template>

<script>
import dayjs from 'dayjs'
import { toRefs } from '@vue/composition-api'
import { isToday } from '@ms/utils/time'
import { getLabelIcon } from '@ms/stores/matchLabels'
import Outcome from 'wap/match/Event/pagelet/Outcome'
import useMatch from '@ms/components/match/useMatch.js'
import { getEventShowScores } from '@ms/utils/eventHandlers/event-stat'
import { prefillMatchSummary } from '@ms/components/match/useMatchInfo.js'
import IconPlay from 'assets/theme/IconPlay.vue'
import IconHot from 'assets/theme/IconHot.vue'
import IconCommentary from 'assets/theme/IconCommentary.vue'
import 'base/icons/svg/arrowright-new.svg'

export default {
  name: 'FeaturedEvent',
  components: {
    Outcome,
    IconPlay,
    IconHot,
    IconCommentary
  },
  props: {
    event: {
      required: true,
      type: Object
    },
    backgroundImage: {
      type: String,
      default: ''
    },
    count: {
      type: Number,
      default: 0
    }
  },
  setup(props) {
    const { event } = toRefs(props)

    const { isLive, isBooked, matchTime, matchLink, matchStage } = useMatch({
      event,
      excludeStages: ['H1', 'H2'],
      timeOptions: {
        autoplay: false
      }
    })

    const prefillMatchDetail = () => {
      prefillMatchSummary(props.event)
      window.mstrace('Home_FeatureMatch_ClickMatchdetail')
    }

    const handleOdds = () => {
      window.mstrace('Home_FeatureMatch_ClickSelection')
    }

    const handleMore = () => {
      window.mstrace('FeatureMatch_MoreEvents')
    }

    return {
      isLive,
      isBooked,
      matchTime,
      matchLink,
      matchStage,
      prefillMatchDetail,
      handleOdds,
      handleMore
    }
  },
  computed: {
    // home & away logo 同时配置了才显示
    teamLogoVisible() {
      return this.event.homeTeamIcon && this.event.awayTeamIcon
    },
    market() {
      const { markets = {} } = this.event
      return markets.map['1']
    },
    outcomes() {
      return this.market.outcomes
    },
    marketTitles() {
      if (this.market && this.market.title) {
        return this.market.title.split(',')
      }
      return ['1', 'x', '2']
    },
    scores() {
      if (this.event && this.isLive) {
        const scoreArr = getEventShowScores(this.event)
        if (scoreArr) {
          return scoreArr[0]
        }
      }
      return []
    },
    showDate() {
      const { startTime } = this.event
      if (isToday(startTime)) {
        return 'Today'
      }
      return dayjs(startTime).format('DD/MM')
    },
    matchLabelTitle() {
      return getLabelIcon({
        configKey: 'sportsListName',
        displayIn: '3',
        sportId: this.event.sportId,
        tournamentId: this.event.tournamentId,
        eventStatus: this.event.status
      })
    }
  }
}
</script>

<style lang="less">
@import url("~wap/common/style/variable.less");
@import url("~wap/common/style/mixin.less");

.m-featured-event {
  padding: 0 15px 9px;
  border: 1px solid #222;
  border-radius: 8px;
  background: #222;
  background-position: right center;
  background-repeat: no-repeat;
  background-size: contain;
  transform: translateZ(0); // 启用 gpu 加速（背景图 cpu 占用太高）

  .m-featured-event--header {
    display: flex;
    height: 28px;
    align-items: center;

    .m-title {
      margin-right: 4px;
      color: #666;
      font-size: 10px;
    }

    .label-tag {
      height: 14px;
      margin-right: 4px;
    }

    .tag-commentary {
      color: #000;
    }
  }

  .m-sport-link {
    display: block;
  }

  .m-featured-event--info {
    display: flex;
    justify-content: space-between;

    .teams-wrap {
      width: 150/168*100%;
      flex: 1;
    }

    .m-score-group {
      width: 20px;
      flex: none;
      color: @primary;
      font-size: 16px;
      font-weight: 700;
      line-height: 16px;
      text-align: right;

      .away-score {
        margin-top: 4px;
      }
    }

    .m-team {
      display: flex;
      height: 16px;
      align-items: center;
      line-height: 16px;

      &:first-child {
        margin-bottom: 4px;
      }

      &-logo {
        width: 16px;
        margin-right: 4px;
      }

      &-name {
        flex: 1;
        color: #f8f8f8;
        font-size: 12px;
        line-height: 14px;
      }
    }

    .m-time {
      color: #fff;
      font-size: 20px;
      font-weight: 700;
      line-height: 24px;

      &.m-live-time {
        color: #ccc;
        font-size: 12px;
        font-weight: 400;
      }
    }

    .m-date {
      color: #ccc;
      font-size: 8px;
      line-height: 12px;
      text-align: right;
    }
  }

  .m-featured-event--outcomes {
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 8px 0 10px;

    .m-outcome {
      height: 28px;
      flex: 1;
      border-radius: 4px;
      margin: 0 4px 0 0;
      line-height: 28px;

      &:last-child {
        margin-right: 0;
      }

      .desc {
        font-weight: 500;
        text-align: left;
      }

      .odds {
        font-family: Roboto;
      }
    }
  }

  .featured-event--footer {
    text-align: center;

    .m-view-more {
      display: block;
      height: 14px;
      color: #999;
      font-size: 10px;
      line-height: 14px;

      .svg-icon {
        margin-top: -2px;
        font-size: 10px;
      }
    }
  }
}
</style>

<style lang="scss">
[data-mode="light"] .m-featured-event {
  border-color: #FFF0C4;
  background: #FFF;

  .m-featured-event--header {
    .m-title {
      color: #999;
    }
  }

  .m-featured-event--info {
    .m-team {
      &-name {
        color: #000;
      }
    }

    .m-score-group {
      color: #000;
    }

    .m-time {
      color: #000;

      &.m-live-time {
        color: #333;
      }
    }

    .m-date {
      color: #4A4A4A;
    }
  }

  .m-featured-event--outcomes {
    .m-outcome {
      &.multiple {
        .desc {
          color: #999;
        }

        .odds {
          color: #000;
        }

        &.active {
          .desc {
            color: #000;
          }
        }
      }
    }
  }
}
</style>
